/**
 * @ngdoc service
 * @name tempModelerUrlService
 * @module flowingly.services
 *
 * @description A service responsible solely for opening the modeler in a new tab
 *
 * ## Notes
 * It is expected that this will be a temporary service
 */
import { SharedAngular } from '@Angular.Runner/@types/sharedAngular';
import FlowModelType from '@Shared.Angular/@types/core/contracts/constants/flowModelType';
import { Guid } from '@Shared.Angular/@types/guid';

export interface IGetModelerUrlParams {
  flowModelId?: Guid;
  cloneModel?: boolean;
  openFeedback?: boolean;
  templateKey?: string | null;
  loadPublishRequestSchema?: boolean;
  enableMapAutomation?: boolean;
  flowModelType?: FlowModelType | null;
  generateWithAi?: boolean;
  schemaId?: Guid | null;
  generationFileId?: Guid | null;
}

angular
  .module('flowingly.services')
  .factory('tempModelerUrlService', tempModelerUrlService);

tempModelerUrlService.$inject = [
  'APP_CONFIG',
  'guidService',
  'sessionService',
  'flowinglyConstants'
];

function tempModelerUrlService(
  APP_CONFIG: SharedAngular.APP_CONFIG,
  guidService: SharedAngular.GuidService,
  sessionService: SharedAngular.SessionService,
  flowinglyConstants: SharedAngular.FlowinglyConstants
) {
  const service = {
    openModeler: openModeler,
    getModelerUrl: getModelerUrl,
    openLinkInNewTab: openLinkInNewTab,
    openReadOnlyVersionInNewTab: openReadOnlyVersionInNewTab
  };

  return service;

  /// Public API Methods /////////////////////////////////////////////////////////////////

  function openModeler(params: IGetModelerUrlParams = {}) {
    const url = getModelerUrl(params);
    openLinkInNewTab(url);
  }

  function getModelerUrl(params: IGetModelerUrlParams): string {
    const {
      flowModelId = guidService.empty(),
      cloneModel = false,
      openFeedback = false,
      templateKey = null,
      loadPublishRequestSchema = false,
      enableMapAutomation = false,
      flowModelType = null,
      generateWithAi = false,
      schemaId = null,
      generationFileId = null
    } = params;

    let modelerPath =
      `/modeler/#/workflow?workflowId=${flowModelId}` +
      `&clone=${cloneModel}` +
      `&openFeedback=${openFeedback}` +
      (templateKey ? `&templateKey=${templateKey}` : '') +
      (loadPublishRequestSchema
        ? `&loadPublishRequestSchema=${loadPublishRequestSchema}`
        : '') +
      (enableMapAutomation
        ? `&enableMapAutomation=${enableMapAutomation}`
        : '') +
      (flowModelType != null ? `&flowModelType=${flowModelType}` : '') +
      (generateWithAi ? `&generateWithAi=${generateWithAi}` : '');

    if (schemaId) {
      modelerPath += `&schemaId=${schemaId}`;
    }

    if (generationFileId) {
      modelerPath += `&generationFileId=${generationFileId}`;
    }

    if (
      APP_CONFIG.flowinglyEnvironment ===
      flowinglyConstants.flowinglyEnvironments.Local
    ) {
      const token = sessionService.getToken();
      return APP_CONFIG.modelerUrl + modelerPath + `&token=${token}`;
    } else {
      return modelerPath;
    }
  }

  function openReadOnlyVersionInNewTab(flowModelId: Guid, schemaId: Guid) {
    const url = getModelerUrl({
      flowModelId,
      schemaId
    });
    openLinkInNewTab(url);
  }

  //firefox & IE11 need a DOM click event to be fired!
  // otherwise simply a.click() would work with Chrome etc.
  function openLinkInNewTab(url) {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    if (document.createEvent) {
      const ev = document.createEvent('MouseEvent');
      ev.initMouseEvent(
        'click',
        true /* bubble */,
        true /* cancelable */,
        window,
        null,
        0,
        0,
        0,
        0 /* coordinates */,
        false,
        false,
        false,
        false /* modifier keys */,
        0 /*left*/,
        null
      );
      a.dispatchEvent(ev);
    } else {
      a.click();
    }
  }
}

export type TempModelerUrlServiceType = ReturnType<
  typeof tempModelerUrlService
>;
